import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import { MiDatePicker, MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { CalendarType, ICalendarConfig, ICalendarSet } from "@miplatform/types";
import {
  addToBusinessDate,
  getDate,
  toFormattedDate,
  toSnappedTime,
} from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiHandBackRightOutline } from "@mdi/js";
import { Box, useTheme } from "@mui/material";

const HOLD_UNITS = ["Hours", "Days"] as const;

interface WorkOrderHoldModalProps {
  open: boolean;
  onClose: () => void;
  onHold: (data: {
    contactName: string;
    reason: string;
    description: string;
    followUpDate: string;
    calendarId: string;
  }) => Promise<void>;
  isLoading: boolean;
  calendarSet: ICalendarSet | null;
}

interface HoldFormValues {
  contactName: string;
  reason: string;
  followUpDate: Date | null;
  followUpTime: string | null;
  calendarId: string;
  overrideHoldDuration: boolean;
  holdUnit: (typeof HOLD_UNITS)[number];
  holdValue: number | null;
}

const holdSchema = z.object({
  contactName: z.string().min(1, "Contact Name is required."),
  calendarId: z.string().min(1, "Hold Type is required."),
  reason: z.string().min(1, "Reason is required."),
  followUpDate: z.date().nullable(),
  followUpTime: z.string().nullable(),
  overrideHoldDuration: z.boolean(),
  holdUnit: z.enum(HOLD_UNITS),
  holdValue: z.number().nullable(),
});

export const WorkOrderHoldModal = ({
  open,
  onClose,
  onHold,
  isLoading,
  calendarSet,
}: WorkOrderHoldModalProps) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const initialHoldValues: HoldFormValues = useMemo(
    () => ({
      contactName: "",
      reason: "",
      followUpDate: null,
      followUpTime: null,
      calendarId: "",
      overrideHoldDuration: false,
      holdUnit: "Hours",
      holdValue: null,
    }),
    []
  );

  const { setValue, control, reset, handleSubmit, getValues } =
    useForm<HoldFormValues>({
      mode: "onChange",
      defaultValues: initialHoldValues,
      resolver: zodResolver(holdSchema),
    });

  const calendarId = useWatch({ control, name: "calendarId" });
  const overrideHoldDuration = useWatch({
    control,
    name: "overrideHoldDuration",
  });
  const followUpDate = useWatch({ control, name: "followUpDate" });
  const contactName = useWatch({ control, name: "contactName" });
  const reason = useWatch({ control, name: "reason" });
  const holdValue = useWatch({ control, name: "holdValue" });
  const holdUnit = useWatch({ control, name: "holdUnit" });

  const holdCalendarList = useMemo(() => {
    return calendarSet?.calendarConfigs
      ?.filter(
        (calendar: ICalendarConfig) =>
          calendar.calendarType === CalendarType.HoldsList
      )
      .map((cal: ICalendarConfig) => ({
        ...cal,
        value: cal.id,
        label: cal.name,
      }));
  }, [calendarSet]);

  const selectedCalendar = useMemo(
    () => holdCalendarList?.find((x) => x.id === calendarId),
    [holdCalendarList, calendarId]
  );

  const followUpDefaultOffsetHours =
    selectedCalendar?.followUpDefaultOffsetHours || null;

  const getDefaultHoldDateTimeUpdates = useCallback(() => {
    if (!selectedCalendar || followUpDefaultOffsetHours === null) {
      return {
        followUpDate: null,
        followUpTime: null,
        holdValue: null,
      };
    }
    const followUpDateTime = addToBusinessDate(
      getDate(),
      Number(followUpDefaultOffsetHours),
      "hours"
    );
    const snappedDateTime = toSnappedTime(followUpDateTime.toDate()).toDate();
    return {
      followUpDate: snappedDateTime,
      followUpTime: toFormattedDate(
        snappedDateTime,
        DATE_TIME_FORMATS.FORMAT_24_HOUR
      ),
      holdValue: Number(followUpDefaultOffsetHours),
    };
  }, [selectedCalendar, followUpDefaultOffsetHours]);

  useEffect(() => {
    const updates = getDefaultHoldDateTimeUpdates();
    setValue("followUpDate", updates.followUpDate);
    setValue("followUpTime", updates.followUpTime);
    setValue("holdValue", updates.holdValue);
  }, [calendarId, getDefaultHoldDateTimeUpdates, setValue]);

  const handleHoldUnitChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHoldUnit = e.target.value as (typeof HOLD_UNITS)[number];
      const currentHoldValue = getValues("holdValue");
      const updatedMoment = addToBusinessDate(
        getDate(),
        currentHoldValue || 0,
        newHoldUnit
      );
      const snappedDateTime = toSnappedTime(updatedMoment.toDate()).toDate();

      setValue("holdUnit", newHoldUnit, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue("followUpDate", snappedDateTime, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        "followUpTime",
        toFormattedDate(snappedDateTime, DATE_TIME_FORMATS.FORMAT_24_HOUR),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [setValue, getValues]
  );

  const handleHoldValueChange = useCallback(
    ({ event: e = {} }: { event?: { target?: { value?: string } } }) => {
      const newHoldValue = e.target?.value ? Number(e.target.value) : null;
      const currentHoldUnit = getValues("holdUnit");
      const updatedMoment = addToBusinessDate(
        getDate(),
        newHoldValue || 0,
        currentHoldUnit
      );
      const snappedDateTime = toSnappedTime(updatedMoment.toDate()).toDate();

      setValue("holdValue", newHoldValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue("followUpDate", snappedDateTime, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        "followUpTime",
        toFormattedDate(snappedDateTime, DATE_TIME_FORMATS.FORMAT_24_HOUR),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [setValue, getValues]
  );

  const handleOverrideChange = useCallback(
    (e: any) => {
      const checked = e.target ? e.target.checked : e;
      if (checked) {
        setValue("overrideHoldDuration", checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("holdUnit", "Hours", {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        setValue("overrideHoldDuration", checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("holdUnit", "Hours", {
          shouldValidate: true,
          shouldDirty: true,
        });
        const updates = getDefaultHoldDateTimeUpdates();
        setValue("followUpDate", updates.followUpDate, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("followUpTime", updates.followUpTime, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("holdValue", updates.holdValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [setValue, getDefaultHoldDateTimeUpdates]
  );

  const holdDetailFields = useMemo(
    () => [
      {
        label: "Contact/Customer Name",
        name: "contactName",
        required: true,
        width: "100%",
        formControl: control,
        style: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      {
        label: "Hold Type",
        name: "calendarId",
        fieldType: INPUT_TYPES.SELECT,
        style: { paddingLeft: 0, paddingRight: 0 },
        width: "100%",
        options: holdCalendarList || [],
        required: true,
        formControl: control,
      },
      {
        label: "Reason",
        name: "reason",
        width: "100%",
        required: true,
        formControl: control,
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Hold Unit",
        name: "holdUnit",
        fieldType: INPUT_TYPES.SELECT,
        className: classes.orangeInputBox,
        options: HOLD_UNITS.map((unit) => ({
          label: unit,
          value: unit,
        })),
        handleChange: handleHoldUnitChange,
        value: holdUnit,
        style: {
          paddingLeft: 0,
          paddingRight: theme.spacing(0.5),
        },
      },
      {
        label: "Hold Value",
        name: "holdValue",
        fieldType: INPUT_TYPES.NUMERIC,
        allowNegative: false,
        thousandSeparator: true,
        allowLeadingZeros: false,
        decimalScale: 0,
        handleChange: handleHoldValueChange,
        value: holdValue,
        style: {
          paddingLeft: theme.spacing(2),
          paddingRight: 0,
        },
        inputTextboxProps: {
          className: classes.orangeInputBox,
        },
      },
      {
        label: "Follow-Up Date",
        name: "followUpDate",
        fieldType: INPUT_TYPES.CUSTOM,
        formControl: control,
        style: {
          paddingLeft: 0,
          paddingTop: theme.spacing(2),
          paddingRight: theme.spacing(1.2),
        },
        customComponent: (
          <MiDatePicker
            onChange={(val: Date | null) => setValue("followUpDate", val)}
            value={followUpDate}
          />
        ),
        disabled: true,
      },
      {
        label: "Follow-Up Time",
        name: "followUpTime",
        fieldType: INPUT_TYPES.TIME,
        formControl: control,
        style: {
          paddingLeft: theme.spacing(1),
          paddingRight: 0,
          paddingTop: theme.spacing(1),
        },
        sx: {
          "& .MuiInputLabel-root": {
            left: theme.spacing(1),
          },
        },
        disabled: true,
      },
      {
        label: "Override default hold duration",
        name: "overrideHoldDuration",
        fieldType: INPUT_TYPES.CHECKBOX,
        handleChange: handleOverrideChange,
        value: overrideHoldDuration,
        width: "100%",
        style: { padding: theme.spacing(0.5, 0) },
      },
    ],
    [
      control,
      holdCalendarList,
      classes.orangeInputBox,
      theme,
      handleHoldUnitChange,
      handleHoldValueChange,
      handleOverrideChange,
      setValue,
      followUpDate,
      holdValue,
      holdUnit,
      overrideHoldDuration,
    ]
  );

  const getDisplayedFields = useCallback(() => {
    if (overrideHoldDuration) {
      return [
        ...holdDetailFields.slice(0, 3),
        holdDetailFields[holdDetailFields.length - 1],
        ...holdDetailFields.slice(3, -1),
      ];
    }
    return holdDetailFields.filter(
      ({ name }) => !["holdUnit", "holdValue"].includes(name)
    );
  }, [holdDetailFields, overrideHoldDuration]);

  const handleFormSubmit = async (data: HoldFormValues) => {
    await onHold({
      contactName: data.contactName,
      reason: data.reason,
      description: data.reason,
      followUpDate: data.followUpDate
        ? toFormattedDate(
            data.followUpDate,
            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
          )
        : "",
      calendarId: data.calendarId,
    });
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const isFormValid = !!contactName && !!calendarId && !!reason;

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={448}
      titleIcon={mdiHandBackRightOutline}
      leftIconColor={theme.palette.purple.dark}
      title="Hold Work Order"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Hold Work Order",
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          disabled: isLoading || !isFormValid,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <Box component="form" sx={{ mt: 2 }}>
        <MiDetailFields detailFields={getDisplayedFields()} />
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  orangeInputBox: {
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.orange.light,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.main,
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.dark,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.main,
    },
  },
}));
